import React, { useState, useEffect, useCallback } from "react";
import { staffAPI } from "../../services/api";
import SessionTimer from "./SessionTimer";
import { handleApiError } from "../../utils/errorUtils";
import { useCompletedTimers } from "../../context/CompletedTimersContext";
import { useFocusedPoll } from "../../hooks/useFocusedPoll";
import StaffLayout from "./StaffLayout";
import "./ActiveSessions.css";

const ActiveSessions = () => {
  const [activeSessions, setActiveSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshInterval, setRefreshInterval] = useState(30); // seconds
  const { removeCompletedTimer } = useCompletedTimers();

  // Fetch active sessions
  const fetchActiveSessions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await staffAPI.getBookings({ status: "in-progress" });
      setActiveSessions(response.data.bookings || []);
      setError(null);
    } catch (err) {
      handleApiError(
        err,
        "ActiveSessions.fetchActiveSessions",
        (message) => setError(message),
        "Error fetching active sessions"
      );
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial mount-time fetch. The visibility-aware poll + focus/online
  // refetch is wired via `useFocusedPoll` below.
  useEffect(() => {
    fetchActiveSessions();
  }, [fetchActiveSessions]);

  // Visibility-aware polling + focus/online refetch. Paused when the
  // tab is hidden; immediate catch-up refetch on visible/focus/online.
  useFocusedPoll(fetchActiveSessions, refreshInterval * 1000, [
    refreshInterval,
  ]);

  // Handle updating session duration
  const handleDurationChange = async (bookingId, newDuration) => {
    try {
      await staffAPI.updateSessionDuration(bookingId, newDuration);
      // Update the local state to avoid a full refresh
      setActiveSessions((prevSessions) =>
        prevSessions.map((session) =>
          session._id === bookingId
            ? { ...session, duration: newDuration }
            : session
        )
      );
    } catch (err) {
      handleApiError(
        err,
        "ActiveSessions.handleDurationChange",
        (message) => setError(message),
        "Error updating session duration"
      );
    }
  };

  // Handle completing a session
  const handleCompleteSession = async (bookingId) => {
    try {
      await staffAPI.completeBooking(bookingId);
      // Remove the completed session from the list
      setActiveSessions((prevSessions) =>
        prevSessions.filter((session) => session._id !== bookingId)
      );

      // Remove from completed timers context
      removeCompletedTimer(bookingId);
    } catch (err) {
      handleApiError(
        err,
        "ActiveSessions.handleCompleteSession",
        (message) => setError(message),
        "Error completing session"
      );
    }
  };

  // Filter to ensure only in-progress sessions with startedAt timestamps are displayed
  const validSessions = activeSessions.filter(
    (session) =>
      session.status.toLowerCase() === "in-progress" && session.startedAt
  );

  // Group sessions by suite type for better organization
  const groupedSessions = {
    sauna: validSessions.filter(
      (session) => session.sessionType === "infrared"
    ),
    redlight: validSessions.filter(
      (session) => session.sessionType === "redlight"
    ),
    massage: validSessions.filter((session) =>
      ["massage_60", "massage_90"].includes(session.sessionType)
    ),
  };

  const getDefaultDuration = (sessionType) => {
    if (sessionType === "infrared") return 50;
    if (sessionType === "redlight") return 20;
    if (sessionType === "massage_60") return 60;
    if (sessionType === "massage_90") return 90;
    return 30;
  };

  const getSessionLabel = (sessionType) => {
    if (sessionType === "massage_60") return "60 Minute Massage";
    if (sessionType === "massage_90") return "90 Minute Massage";
    return "Massage";
  };

  return (
    <StaffLayout>
      <div className="active-sessions-container">
        <div className="active-sessions-header">
          <h2>Active Sessions</h2>
          <div className="active-sessions-controls">
            <button
              className="refresh-btn"
              onClick={fetchActiveSessions}
              disabled={loading}
            >
              {loading ? "Refreshing..." : "Refresh"}
            </button>
            <div className="refresh-interval">
              <label htmlFor="refreshInterval">Auto-refresh: </label>
              <select
                id="refreshInterval"
                value={refreshInterval}
                onChange={(e) => setRefreshInterval(Number(e.target.value))}
              >
                <option value={15}>15 seconds</option>
                <option value={30}>30 seconds</option>
                <option value={60}>1 minute</option>
                <option value={300}>5 minutes</option>
              </select>
            </div>
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}

        {loading && activeSessions.length === 0 ? (
          <div className="loading-message">Loading active sessions...</div>
        ) : validSessions.length === 0 ? (
          <div className="no-sessions-message">
            <p>No in-progress sessions at the moment.</p>
            <p className="no-sessions-hint">
              Sessions must be started to appear here.
            </p>
          </div>
        ) : (
          <div className="sessions-by-type">
            {/* Sauna Sessions */}
            {groupedSessions.sauna.length > 0 && (
              <div className="session-type-group">
                <h3>
                  Infrared Sauna Sessions ({groupedSessions.sauna.length})
                </h3>
                <div className="active-sessions-grid">
                  {groupedSessions.sauna.map((session) => (
                    <SessionTimer
                      key={session._id}
                      booking={session}
                      onDurationChange={handleDurationChange}
                      onComplete={handleCompleteSession}
                      defaultDuration={50}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Red Light Sessions */}
            {groupedSessions.redlight.length > 0 && (
              <div className="session-type-group">
                <h3>Red Light Sessions ({groupedSessions.redlight.length})</h3>
                <div className="active-sessions-grid">
                  {groupedSessions.redlight.map((session) => (
                    <SessionTimer
                      key={session._id}
                      booking={session}
                      onDurationChange={handleDurationChange}
                      onComplete={handleCompleteSession}
                      defaultDuration={20}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Massage Sessions */}
            {groupedSessions.massage.length > 0 && (
              <div className="session-type-group">
                <h3>Massage Sessions ({groupedSessions.massage.length})</h3>
                <div className="active-sessions-grid">
                  {groupedSessions.massage.map((session) => (
                    <SessionTimer
                      key={session._id}
                      booking={{
                        ...session,
                        displayLabel: getSessionLabel(session.sessionType),
                      }}
                      onDurationChange={handleDurationChange}
                      onComplete={handleCompleteSession}
                      defaultDuration={getDefaultDuration(session.sessionType)}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </StaffLayout>
  );
};

export default ActiveSessions;
